import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logoPng from "@/assets/brema-logo.png";

const DARK = [19, 24, 34];
const GRAY = [110, 116, 128];
const LINE = [225, 226, 230];
const GREEN = [22, 140, 74];

const money = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const dmy = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return "—"; } };
const STATUS_LABEL = { in_elaborazione: "In elaborazione", spedito: "Spedito", completato: "Completato", ricevuto: "In elaborazione" };

const oidOf = (order) => String(order._id || "").slice(-6).toUpperCase();

function addrLines(a) {
  if (!a) return [];
  const l2 = [a.address, a.zip, a.city, a.province ? `(${a.province})` : ""].filter(Boolean).join(" ");
  return [a.recipient, l2, a.country && a.country !== "Italia" ? a.country : ""].filter(Boolean);
}

async function loadLogo() {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const targetH = 150;
        const scale = targetH / img.naturalHeight;
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(img.naturalWidth * scale));
        c.height = targetH;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/png"));
      } catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = logoPng;
  });
}

async function renderOrderPdf(order) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  const oid = oidOf(order);

  // Logo (vertical gray badge)
  const logoH = 50, logoW = (logoH * 349.11) / 865.13;
  try { const d = await loadLogo(); doc.addImage(d, "PNG", M, 34, logoW, logoH); } catch (e) { /* ignore */ }

  // Title next to logo
  const tx = M + logoW + 14;
  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...DARK);
  doc.text("Conferma d'ordine B2B", tx, 58);

  // Order meta (right)
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...DARK);
  doc.text(`Ordine #${oid}`, W - M, 48, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GRAY);
  doc.text(`Data: ${dmy(order.created_at)}`, W - M, 62, { align: "right" });
  doc.text(`Stato: ${STATUS_LABEL[order.status] || "In elaborazione"}`, W - M, 74, { align: "right" });

  // Info blocks
  const colW = (W - M * 2 - 20) / 2;
  const boxY = 106;
  const r = order.reseller || {};
  const billLines = addrLines(r.billing_address);
  const leftLines = [
    r.company_name || order.reseller_name || "",
    r.vat_number ? `P.IVA ${r.vat_number}` : "",
    ...billLines.slice(1),
    r.email || "",
    r.phone || "",
  ].filter(Boolean);
  const shipLines = addrLines(order.shipping_address);

  const drawBlock = (x, title, lines) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...GRAY);
    doc.text(title.toUpperCase(), x, boxY);
    doc.setFontSize(9.5); doc.setTextColor(...DARK);
    let yy = boxY + 15;
    lines.forEach((ln, i) => {
      doc.setFont("helvetica", i === 0 ? "bold" : "normal");
      doc.text(String(ln), x, yy, { maxWidth: colW });
      yy += 13;
    });
  };
  drawBlock(M, "Rivenditore / Fatturazione", leftLines.length ? leftLines : ["—"]);
  drawBlock(M + colW + 20, "Destinazione merce", shipLines.length ? shipLines : ["—"]);

  // Items table
  const items = order.items || [];
  autoTable(doc, {
    startY: 190,
    margin: { left: M, right: M },
    head: [["Cod. articolo", "Prodotto", "Qtà", "Prezzo listino", "Sconto", "Prezzo netto", "Totale netto"]],
    body: items.map((it) => [
      it.sku || "—",
      [it.name, [it.size && `Taglia ${it.size}`, it.color].filter(Boolean).join(" · ")].filter(Boolean).join("\n"),
      String(it.quantity),
      money(it.list_price_excl_vat),
      it.discount_pct > 0 ? `-${it.discount_pct}%` : "—",
      money(it.unit_price_excl_vat),
      money(it.line_excl_vat),
    ]),
    styles: { font: "helvetica", fontSize: 9, cellPadding: 6, textColor: DARK, lineColor: LINE, lineWidth: 0.5 },
    headStyles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [247, 248, 250] },
    columnStyles: {
      0: { cellWidth: 78 },
      2: { halign: "center", cellWidth: 34 },
      3: { halign: "right", cellWidth: 66 },
      4: { halign: "right", cellWidth: 46 },
      5: { halign: "right", cellWidth: 66 },
      6: { halign: "right", cellWidth: 70, fontStyle: "bold" },
    },
  });

  // Totals
  let y = doc.lastAutoTable.finalY + 18;
  const tRight = W - M;
  const tLeft = W - M - 200;
  const row = (label, val, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 12 : 10);
    doc.setTextColor(...DARK);
    doc.text(label, tLeft, y);
    doc.text(val, tRight, y, { align: "right" });
    y += bold ? 20 : 15;
  };
  if (Number(order.volume_discount_amount || 0) > 0) {
    row("Subtotale (listino)", money(order.list_subtotal_excl_vat));
    row(`Sconto volume -${order.volume_discount_pct}%`, `-${money(order.volume_discount_amount)}`);
  }
  row("Imponibile", money(order.subtotal_excl_vat));
  const isFreeShip = order.shipping_is_free || Number(order.shipping_excl_vat || 0) === 0;
  const shipSaved = Number(order.shipping_flat_cost || 0);
  if (isFreeShip) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...DARK);
    doc.text("Trasporto", tLeft, y);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...GREEN);
    doc.text("Gratuito", tRight, y, { align: "right" });
    y += 14;
    if (shipSaved > 0) {
      doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(...GREEN);
      doc.text(`Spedizione gratuita · risparmi ${money(shipSaved)}`, tRight, y, { align: "right" });
      y += 13;
    }
    doc.setTextColor(...DARK);
  } else {
    row("Trasporto", money(order.shipping_excl_vat));
  }
  if (order.vat_exempt) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...DARK);
    doc.text("IVA", tLeft, y);
    doc.setFont("helvetica", "italic"); doc.setTextColor(...GRAY);
    doc.text("Non imponibile", tRight, y, { align: "right" });
    y += 15;
    doc.setTextColor(...DARK);
    row("TOTALE (IVA esente)", money(order.total_incl_vat ?? order.total), true);
    if (order.vat_exempt_reason) {
      doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...GRAY);
      doc.text(order.vat_exempt_reason, M, y + 4, { maxWidth: W - M * 2 });
      y += 20;
    }
  } else {
    row("IVA", money(order.vat_total));
    row("TOTALE (IVA incl.)", money(order.total_incl_vat ?? order.total), true);
  }

  if (order.note) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(`Nota: ${order.note}`, M, y + 6, { maxWidth: W - M * 2 });
  }

  // Footer
  const H = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...GRAY);
  doc.text("Brema Bike · Documento generato automaticamente dal Portale Rivenditori B2B.", M, H - 30);

  return doc;
}

export async function generateOrderPdf(order) {
  const doc = await renderOrderPdf(order);
  doc.save(`Ordine_${oidOf(order)}.pdf`);
}

export async function buildOrderPdfBase64(order) {
  const doc = await renderOrderPdf(order);
  // returns "data:application/pdf;base64,...."
  return doc.output("datauristring");
}
